import { useCallback, useEffect, useRef, useState } from "react";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  InfoWindow,
  MarkerClusterer,
  HeatmapLayer,
  Polyline,
  Circle,
  OverlayView,
} from "@react-google-maps/api";
import GoogleMapStyles from "./GoogleMapStyles";
import { useAllContexts } from "../../../hooks/useAllContexts";
import { useLocation } from "react-router-dom";

import green_mark from "./../../../assets/icons/Map/green_mark_icon.svg";
import orange_mark from "./../../../assets/icons/Map/orange_mark_icon.svg";
import blue_mark from "./../../../assets/icons/Map/blue_mark_icon.svg";
import purple_mark from "./../../../assets/icons/Map/purple_mark_icon.svg";
import yellow_mark from "./../../../assets/icons/Map/yellow_mark_icon.svg";
import light_blue_mark from "./../../../assets/icons/Map/light_blue_mark_icon.svg";
import pin_blue from "./../../../assets/icons/Map/pin_blue_icon.svg";
import availableSpot from "../../../assets/images/p1.png";
import occupiedSpot from "../../../assets/images/p2.png";
import SensorsHeatMapButton from "./SensorsHeatMapButton";
import HeatMapButton from "./HeatMapButton";
import styled from "styled-components";
import { getCircleColor, getTextColor } from "../utils";

const cityCenter = {
  lat: 40.576479,
  lng: 22.9587036,
};

const getZoomLevel = (id) => {
  switch (id) {
    case "2":
    case "4":
    case "24":
    case "32":
      return 15;
    case "6":
      return 12;
    default:
      return 14;
  }
};

const SmartIsCityMap = () => {
  const [mapCenter, setMapCenter] = useState(cityCenter);
  const [map, setMap] = useState(null);
  const [currentData, setCurrentData] = useState([]);
  const [currentLoading, setCurrentLoading] = useState(false);
  const [sensorMarkers, setSensorMarkers] = useState([]);
  const [showLegend, setShowLegend] = useState(true);

  const { search } = useLocation();

  const currTypeURL = new URLSearchParams(search).get("typeId");
  const zoomRef = useRef(getZoomLevel(currTypeURL));

  const icons = {
    1: green_mark,
    2: orange_mark,
    3: blue_mark,
    4: purple_mark,
    5: yellow_mark,
    6: light_blue_mark,
    7: pin_blue,
    8: pin_blue,
    9: purple_mark,
    10: orange_mark,
    24: orange_mark,
    30: purple_mark,
    31: purple_mark,
    32: purple_mark,
  };
  const options = {
    imagePath:
      "https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m", // so you must have m1.png, m2.png, m3.png, m4.png, m5.png and m6.png in that folder
  };
  const libraries = ["visualization", "places"];
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: "AIzaSyDQlCnn8F1D0S9ymnWZwc_WFx2ZS6g8w1Q",
    // ...otherOptions
    libraries: libraries,
  });

  const onLoad = useCallback(function callback(map) {
    setMap(map);
  }, []);

  const onUnmount = useCallback(function callback(map) {
    setMap(map);
  }, []);

  const {
    environment,
    meteo,
    parking,
    hydro,
    mesh,
    allSensors,
    variables,
    mobility,
    bins,
  } = useAllContexts();

  const { environmentalLoading, environmentalData } = environment;
  const { meshLoading, MeshData } = mesh;
  const { meteoLoading, meteoInfoData, meteoSensors } = meteo;
  const { hLoading, hydroData } = hydro;
  const { loadingParkingDetails, parkingDetailsData } = parking;
  const { loading, allSensorsData } = allSensors;
  const {
    getSelectedItem,
    setSelectedItem,
    getSelectedSensor,
    showHeatMap,
    showMobility,
    setShowHeatMap,
    setShowMobility,
  } = variables;
  const { mobilityData } = mobility;

  useEffect(() => {
    variables.setSelectedItem(null);
    setShowHeatMap(false);
    setShowMobility(false);
    // TODO check for item id in url
  }, []);

  useEffect(() => {
    switch (currTypeURL) {
      case "1": {
        console.log(environmentalData);
        setCurrentLoading(environmentalLoading);
        setCurrentData(environmentalData);
        break;
      }
      case "2": {
        setCurrentLoading(meshLoading);
        setCurrentData(MeshData);
        break;
      }
      case "5": {
        setCurrentLoading(meteoLoading);
        setCurrentData(meteoInfoData);
        break;
      }
      case "6": {
        setCurrentLoading(hLoading);
        setCurrentData(hydroData);
        break;
      }
      case "9": {
        setCurrentLoading(bins.loading);
        setCurrentData(bins.binsData);
        break;
      }
      case "24": {
        setCurrentLoading(loadingParkingDetails);
        setCurrentData(parkingDetailsData);
        break;
      }
      default: {
        if (allSensorsData) {
          setCurrentLoading(loading);
          setCurrentData(
            allSensorsData?.[currTypeURL]?.data ?? allSensorsData[currTypeURL]
          );
        } else {
          setCurrentData([]);
          setCurrentLoading(false);
        }
        break;
      }
    }
  }, [
    environment,
    meteoLoading,
    loadingParkingDetails,
    hLoading,
    meshLoading,
    loading,
    [currTypeURL]?.loading,
    parkingDetailsData,
  ]);

  useEffect(() => {
    if (getSelectedItem) {
      setMapCenter({
        lat: parseFloat(getSelectedItem?.lat ?? getSelectedItem?.Lat),
        lng: parseFloat(getSelectedItem?.lng ?? getSelectedItem?.Lon),
      });
      zoomRef.current =
        currTypeURL === "24"
          ? 22
          : currTypeURL === "4" ||
            currTypeURL === "9" ||
            currTypeURL === "30" ||
            currTypeURL === "31" ||
            currTypeURL === "32"
          ? 20
          : 16;
    } else {
      zoomRef.current = getZoomLevel(currTypeURL);
      setMapCenter(cityCenter);
    }
  }, [getSelectedItem]);

  useEffect(() => {
    if (currTypeURL === "1" || currTypeURL === "5") {
      const tempItems = [];

      const tempData = currTypeURL === "1" ? environmentalData : meteoInfoData;
      tempData?.forEach((item) => {
        const sensorItem = item?.details?.filter((sItem) => {
          return sItem?.sensorvar_description === getSelectedSensor;
        })[0];

        if (sensorItem) {
          if (currTypeURL === "1") {
            tempItems.push(sensorItem);
          } else {
            tempItems.push({ ...sensorItem, lat: item.lat, lng: item.lng });
          }
        }
        setSensorMarkers(tempItems);
      });
    }
  }, [getSelectedSensor]);

  const renderPopup = (item) => {
    switch (currTypeURL) {
      case "1":
      case "4":
      case "5":
      case "6":
      case "30":
      case "31":
      case "32":
      case "36":
        return getSelectedItem &&
          getSelectedItem?.mysensor_id === item?.mysensor_id ? (
          <InfoWindow
            onCloseClick={() => {
              variables.setSelectedItem(null);
            }}
            position={{
              lat: parseFloat(item?.lat),
              lng: parseFloat(item?.lng),
            }}
          >
            <p>{getSelectedItem?.mysensor_name}</p>
          </InfoWindow>
        ) : null;
      default:
        return null;
    }
  };

  const renderMarkers = (item, clusterer) => {
    let position;
    let title;

    switch (currTypeURL) {
      case "24":
        position = {
          lat: item?.Lat,
          lng: item?.Lon,
        };
        title = item?.ParkingName;
        break;
      default:
        position = {
          lat: parseFloat(item?.lat),
          lng: parseFloat(item?.lng),
        };
        title = item?.mysensor_name;
        break;
    }
    return (
      <Marker
        key={`marker_${currTypeURL}_${
          item?.ref_id ?? item.mysensor_id ?? item?.id
        }`}
        icon={getIcon(item)}
        position={position}
        title={title}
        animation={4}
        clickable={true}
        clusterer={clusterer}
        onClick={() => {
          setSelectedItem(item);
        }}
      >
        {renderPopup(item)}
      </Marker>
    );
  };

  const getIcon = (item) => {
    if (currTypeURL === "24") {
      return item?.icon?.includes("p1") ? availableSpot : occupiedSpot;
    } else {
      return icons[currTypeURL];
    }
  };

  const createGradientCircles = (center, baseColor, keyPrefix = "") => {
    const layers = [
      { radius: 200, opacity: 0.7 },
      { radius: 300, opacity: 0.6 },
      { radius: 400, opacity: 0.5 },
      { radius: 500, opacity: 0.4 },
      { radius: 600, opacity: 0.3 },
      { radius: 700, opacity: 0.2 },
      { radius: 800, opacity: 0.1 },
    ];

    return layers.map((layer, index) => (
      <Circle
        key={`${keyPrefix}_circle_layer_${index}`}
        center={center}
        radius={layer.radius}
        options={{
          strokeOpacity: 0,
          fillColor: baseColor,
          fillOpacity: layer.opacity,
          clickable: false,
        }}
      />
    ));
  };

  const fontSize = (value) => (value.length > 4 ? "10px" : "16px");
  return (
    <>
      {isLoaded && (
        <GoogleMap
          center={mapCenter}
          zoom={zoomRef.current}
          onLoad={onLoad}
          onUnmount={onUnmount}
          onDragEnd={() => setMapCenter(map.getCenter().toJSON())}
          onZoomChanged={() => map && (zoomRef.current = map.getZoom())}
          options={{
            styles: GoogleMapStyles,
          }}
        >
          {!showHeatMap &&
            !showMobility &&
            (currTypeURL === "1" || currTypeURL === "5") && (
              <SensorsHeatMapButton id={currTypeURL} />
            )}
          {currTypeURL === "24" && <HeatMapButton />}
          {currTypeURL === "24" &&
            showHeatMap &&
            !showMobility &&
            !getSelectedSensor && (
              <HeatmapLayer
                data={currentData?.map(
                  (marker) =>
                    new window.google.maps.LatLng(marker.Lat, marker.Lon)
                )}
              />
            )}
          {!showHeatMap && !getSelectedSensor && !showMobility && (
            <MarkerClusterer
              options={options}
              averageCenter={true}
              clickable={true}
            >
              {(clusterer) =>
                currentData?.map((item) => renderMarkers(item, clusterer)) || []
              }
            </MarkerClusterer>
          )}

          {currTypeURL === "24" && showMobility && (
            <MobilityLegend $showLegend={showLegend}>
              <span
                style={{
                  textAlign: "center",
                  // padding: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
                onClick={() => setShowLegend(!showLegend)}
              >
                {showLegend ? "Ελαχιστοποίηση" : "Προβολή υπομνήματος"}
              </span>
              <hr style={{ width: "100%", borderColor: "rgba(0,0,0,0.1)" }} />
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "10px",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <p>Πολύ Χαμηλό:</p>
                <div
                  style={{
                    width: "40px",
                    height: "10px",
                    backgroundColor: "#0fd170",
                  }}
                ></div>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "10px",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <p>Χαμηλό:</p>
                <div
                  style={{
                    width: "40px",
                    height: "10px",
                    backgroundColor: "#fcb836",
                  }}
                ></div>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "10px",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <p>Μέτριο:</p>
                <div
                  style={{
                    width: "40px",
                    height: "10px",
                    backgroundColor: "#ff474c",
                  }}
                ></div>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "10px",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <p>Υψηλό:</p>
                <div
                  style={{
                    width: "40px",
                    height: "10px",
                    backgroundColor: "#ff0038",
                  }}
                ></div>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "10px",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <p>Πολύ Υψηλό:</p>
                <div
                  style={{
                    width: "40px",
                    height: "10px",
                    backgroundColor: "#880038",
                  }}
                ></div>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "10px",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <p>Μη Διαθέσιμο:</p>
                <div
                  style={{
                    width: "40px",
                    height: "10px",
                    backgroundColor: "#aaa",
                  }}
                ></div>
              </div>
            </MobilityLegend>
          )}

          {currTypeURL === "24" &&
            showMobility &&
            mobilityData?.map((line, id) => {
              console.log("TEST: line: ", line);
              return (
                <Polyline
                  path={line.route.geometry.coordinates}
                  options={{
                    strokeColor:
                      line.level === 0
                        ? "#0fd170"
                        : line.level === 1
                        ? "#fcb836"
                        : line.level === 2
                        ? "#ff474c"
                        : line.level === 3
                        ? "#ff0038"
                        : line.level === 4
                        ? "#880038"
                        : "#aaa",
                    strokeOpacity: 1, // 50% opacity
                    strokeWeight: 4, // Line thickness
                  }}
                  geodesic={true}
                />
              );
            })}

          {getSelectedSensor &&
            sensorMarkers?.map((sensor, id) => {
              return (
                <OverlayView
                  key={`overlay_label_${sensor.sensor_id}`}
                  position={{
                    lat: parseFloat(sensor.lat),
                    lng: parseFloat(sensor.lng),
                  }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <div
                    style={{
                      transform: "translate(-50%, -50%)",
                      fontSize: fontSize(
                        parseFloat(sensor.measurement).toFixed(1)
                      ),
                      fontWeight: "bold",
                      color: getTextColor(
                        getCircleColor(
                          parseFloat(sensor.measurement).toFixed(1),
                          getSelectedSensor
                        )
                      ),
                      background:
                        getSelectedSensor !== "Κατεύθυνση ανέμου"
                          ? "rgba(10,10,10,0.1)"
                          : "rgb(200,200,255)",
                      width: "40px",
                      height: "40px",
                      borderRadius: "40px",
                      pointerEvents: "none",
                      whiteSpace: "nowrap",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {parseFloat(sensor.measurement).toFixed(1)}
                  </div>
                </OverlayView>
              );
            })}

          {getSelectedSensor &&
            sensorMarkers?.map((sensor) => {
              return createGradientCircles(
                {
                  lat: parseFloat(sensor.lat),
                  lng: parseFloat(sensor.lng),
                },
                getCircleColor(
                  parseFloat(sensor.measurement).toFixed(2),
                  getSelectedSensor
                ),
                sensor.sensorvar_id
              );
            })}
        </GoogleMap>
      )}
    </>
  );
};

export default SmartIsCityMap;

const MobilityLegend = styled.div`
  width: 180px;
  height: ${(props) => (props.$showLegend ? "160px" : "18px")};
  overflow: hidden;
  background-color: #fff;
  position: absolute;
  right: 60px;
  bottom: 30px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px;
  border-radius: 4px;
  box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.2);
  transition: all 1s ease;
`;
